import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { Loader2, Download } from "lucide-react";
import { API } from "../../../App";

const money = (v) =>
  v == null ? "—" : `€${Number(v).toLocaleString("en-IE", {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Drill-down behind the P&L "Redo & warranty cost" line.
 *
 * Reads `GET /accounting/redo-warranty-cost` with the SAME period params
 * the P&L used, and that endpoint reads the SAME report `build_pnl` does,
 * attributed by REDO COMPLETION date — so `cost_booked` here equals the
 * figure on the page by construction.
 */
export const RedoWarrantyCostDialog = ({ open, onClose, periodParams }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/accounting/redo-warranty-cost`,
                                { params: periodParams });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load redo costs");
    } finally {
      setLoading(false);
    }
  }, [periodParams]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const exportCsv = () => {
    const qs = new URLSearchParams(periodParams || {}).toString();
    window.open(`${API}/accounting/export/redo-warranty-cost.csv?${qs}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-4xl max-h-[85vh] overflow-y-auto"
                     data-testid="redo-warranty-cost-dialog">
        <DialogHeader>
          <DialogTitle>Redo &amp; warranty cost</DialogTitle>
          <DialogDescription className="text-slate-400">
            {data?.period?.label || "…"} — free return visits to fix our own
            work. A redo has no invoice, so this is additional cost of sales
            in the month the redo was completed. Each one is attributed back
            to the original job and installer without ever moving that job&apos;s
            invoice month.
          </DialogDescription>
        </DialogHeader>

        {loading && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}

        {data && !loading && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                ["Booked in cost of sales", money(data.cost_booked), "text-orange-400"],
                ["Return visits", data.redo_count, "text-white"],
                ["Materials", money(data.materials_total), "text-slate-200"],
                ["Labour", money(data.labour_total), "text-slate-200"],
              ].map(([label, value, tone]) => (
                <div key={label} className="bg-slate-800/60 border border-slate-700 rounded-lg p-3">
                  <p className="text-xs text-slate-400">{label}</p>
                  <p className={`text-lg font-semibold ${tone}`}>{value}</p>
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-400" data-testid="redo-hours-total">
              {Number(data.hours_total || 0).toFixed(2)} hours spent on return visits
              in this period.
            </p>

            {(data.by_installer || []).length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">By installer</p>
                <div className="space-y-1">
                  {data.by_installer.map((b) => (
                    <div key={b.installer_name}
                         className="flex justify-between text-sm bg-slate-800/40 rounded px-3 py-2">
                      <span className="text-slate-300">{b.installer_name} · {b.redo_count} visit(s)</span>
                      <span className="text-orange-300">{money(b.cost)} · {Number(b.hours || 0).toFixed(2)}h</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">Every return visit</p>
                <button type="button" onClick={exportCsv}
                        className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
                        data-testid="redo-cost-export-csv">
                  <Download className="w-3 h-3" /> CSV for the accountant
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-400">
                    <tr className="text-left">
                      <th className="py-2 pr-3">Completed</th>
                      <th className="py-2 pr-3">Customer</th>
                      <th className="py-2 pr-3">Reg</th>
                      <th className="py-2 pr-3">Installer</th>
                      <th className="py-2 pr-3">Reason</th>
                      <th className="py-2 pr-3 text-right">Materials</th>
                      <th className="py-2 pr-3 text-right">Labour</th>
                      <th className="py-2 pr-3 text-right">Hours</th>
                      <th className="py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.jobs || []).map((j) => (
                      <tr key={j.redo_booking_id} className="border-t border-slate-800">
                        <td className="py-2 pr-3 text-slate-300">{(j.redo_completed_at || "").slice(0, 10)}</td>
                        <td className="py-2 pr-3 text-slate-300">{j.customer_name || "—"}</td>
                        <td className="py-2 pr-3 font-mono text-saturn-orange">{j.vehicle_reg || "—"}</td>
                        <td className="py-2 pr-3 text-slate-300">{j.installer_name}</td>
                        <td className="py-2 pr-3 text-slate-400">{j.reason_label}</td>
                        <td className="py-2 pr-3 text-right text-slate-200">{money(j.materials_cost)}</td>
                        <td className="py-2 pr-3 text-right text-slate-200">
                          {money(j.labour_cost)}
                          {j.labour_reason ? (
                            <span className="block text-[10px] text-slate-500">{j.labour_reason}</span>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3 text-right text-slate-300">{Number(j.hours_spent || 0).toFixed(2)}</td>
                        <td className="py-2 text-right text-orange-300">{money(j.amount)}</td>
                      </tr>
                    ))}
                    {(data.jobs || []).length === 0 && (
                      <tr><td colSpan={9} className="py-4 text-center text-slate-500">
                        No return visits completed in this period.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RedoWarrantyCostDialog;
